import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import EntityForm from '../components/EntityForm/EntityForm';
import formStyles from '../components/EntityForm/EntityForm.module.css';
import HistoryAccordion from '../components/HistoryAccordion/HistoryAccordion';
import {
  getBlock,
  createBlock,
  updateBlock,
  archiveBlock,
  listAllGolfCourses,
  listBlockTypes,
  isAdmin as checkAdmin,
} from '../api';
import useEntityForm from '../hooks/useEntityForm';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Converts an ISO datetime string from the API ("2026-06-15T08:00:00") to the
 * value that <input type="datetime-local"> expects ("2026-06-15T08:00").
 */
function isoToDatetimeLocal(isoStr) {
  if (!isoStr) return '';
  const match = isoStr.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  return match ? match[1] : '';
}

// ---------------------------------------------------------------------------
// Affected-bookings inline error
// ---------------------------------------------------------------------------

function AffectedBookingsError({ bookings }) {
  if (!bookings || bookings.length === 0) return null;
  return (
    <div style={{
      background: 'rgba(229, 84, 75, 0.08)',
      border: '1px solid rgba(229, 84, 75, 0.25)',
      borderRadius: 'var(--radius-sm)',
      padding: '12px 14px',
      fontSize: 13,
      color: 'var(--cp-danger)',
      lineHeight: 1.5,
    }}>
      <strong>Save blocked: this block overlaps active bookings.</strong>
      <p style={{ margin: '6px 0 8px' }}>Resolve these bookings before saving:</p>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {bookings.map((bn) => (
          <li key={bn}>{bn}</li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BlockFormPage
// ---------------------------------------------------------------------------

export default function BlockFormPage({ user }) {
  const { id } = useParams();
  const isAdmin = checkAdmin(user);

  // Form fields
  const [name, setName] = useState('');
  const [blockTypeId, setBlockTypeId] = useState('');
  const [golfCourseId, setGolfCourseId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  // UI state
  const [fieldErrors, setFieldErrors] = useState({});
  const [affectedBookings, setAffectedBookings] = useState(null);

  // Dropdown data
  const [courses, setCourses] = useState([]);
  const [blockTypes, setBlockTypes] = useState([]);

  const {
    isEdit,
    isBusy,
    formError,
    setFormError,
    setIsLoading,
    isArchived,
    setIsArchived,
    pageTitle,
    setPageTitle,
    handleCancel,
    handleArchive,
    handleRestore,
    runSubmit,
  } = useEntityForm({
    id,
    backPath: '/blocks',
    archiveFn: archiveBlock,
    unarchiveFn: (entityId) => updateBlock(entityId, { is_archived: false }),
    newLabel: 'New block',
  });

  // Load dropdown options once
  useEffect(() => {
    listAllGolfCourses()
      .then((data) => setCourses(data.items || []))
      .catch(() => setCourses([]));
    listBlockTypes({ isArchived: false })
      .then((data) => setBlockTypes(data.items || []))
      .catch(() => setBlockTypes([]));
  }, []);

  // Load block data in edit mode
  useEffect(() => {
    if (!isEdit) return;
    setIsLoading(true);
    getBlock(id)
      .then((data) => {
        const block = data.block || data;
        setName(block.name || '');
        setBlockTypeId(String(block.block_type_id || block.block_type?.id || ''));
        setGolfCourseId(String(block.golf_course_id || block.golf_course?.id || ''));
        setStartTime(isoToDatetimeLocal(block.start_time));
        setEndTime(isoToDatetimeLocal(block.end_time));
        setIsArchived(block.is_archived || false);
        setPageTitle(block.name || 'Block');
      })
      .catch((err) => {
        setFormError(err.message || 'Failed to load block.');
        setPageTitle('Block');
      })
      .finally(() => setIsLoading(false));
  }, [id, isEdit]);

  const validate = useCallback(() => {
    const errors = {};
    if (!name.trim())       errors.name         = 'Name is required.';
    if (!blockTypeId)       errors.blockTypeId  = 'Block type is required.';
    if (!golfCourseId)      errors.golfCourseId = 'Golf course is required.';
    if (!startTime)         errors.startTime    = 'Start time is required.';
    if (!endTime)           errors.endTime      = 'End time is required.';
    if (startTime && endTime && endTime <= startTime) {
      errors.endTime = 'End time must be after start time.';
    }
    return errors;
  }, [name, blockTypeId, golfCourseId, startTime, endTime]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError('');
      setAffectedBookings(null);

      const errors = validate();
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setFieldErrors({});
      const payload = {
        name: name.trim(),
        block_type_id: Number(blockTypeId),
        golf_course_id: Number(golfCourseId),
        start_time: startTime,
        end_time: endTime,
      };
      await runSubmit(
        () => isEdit ? updateBlock(id, payload) : createBlock(payload),
        (err) => {
          if (err.affectedBookings && err.affectedBookings.length > 0) {
            setAffectedBookings(err.affectedBookings);
          } else {
            setFormError(err.message || 'Something went wrong. Please try again.');
          }
        }
      );
    },
    [id, isEdit, name, blockTypeId, golfCourseId, startTime, endTime, validate, runSubmit, setFormError]
  );

  return (
    <EntityForm
      title={pageTitle}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      submitLabel={isEdit ? 'Save changes' : 'Create block'}
      isSubmitting={isBusy}
      error={formError}
      canDelete={isEdit && isAdmin && !isArchived}
      onDelete={handleArchive}
      confirmMessage={`Archive "${name}"? This will remove it from active use.`}
      canRestore={isEdit && isAdmin && isArchived}
      onRestore={() => handleRestore(`Restore "${name}"? It will become active again.`)}
      restoreLabel="Restore block"
      belowCard={isEdit ? <HistoryAccordion entityPath={`/blocks/${id}`} /> : null}
    >
      {/* Affected bookings conflict — non-dismissable, shows above fields */}
      {affectedBookings && (
        <AffectedBookingsError bookings={affectedBookings} />
      )}

      {/* Name — full width */}
      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="blockName">
          Name <span className={formStyles.required} aria-hidden="true">*</span>
        </label>
        <input
          id="blockName"
          type="text"
          className={`${formStyles.input} ${fieldErrors.name ? formStyles.inputError : ''}`}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (fieldErrors.name) setFieldErrors((prev) => ({ ...prev, name: '' }));
          }}
          placeholder="e.g. Tournament — North Links"
          disabled={isBusy}
          autoFocus={!isEdit}
          required
        />
        {fieldErrors.name && (
          <span className={formStyles.fieldError} role="alert">{fieldErrors.name}</span>
        )}
      </div>

      {/* Block Type + Golf Course — half width each */}
      <div className={formStyles.fieldRow}>
        <div className={formStyles.fieldHalf}>
          <label className={formStyles.label} htmlFor="blockType">
            Block type <span className={formStyles.required} aria-hidden="true">*</span>
          </label>
          <select
            id="blockType"
            className={`${formStyles.select} ${fieldErrors.blockTypeId ? formStyles.inputError : ''}`}
            value={blockTypeId}
            onChange={(e) => {
              setBlockTypeId(e.target.value);
              if (fieldErrors.blockTypeId) setFieldErrors((prev) => ({ ...prev, blockTypeId: '' }));
            }}
            disabled={isBusy}
            required
          >
            <option value="">Select block type…</option>
            {blockTypes.map((bt) => (
              <option key={bt.id} value={String(bt.id)}>{bt.name}</option>
            ))}
          </select>
          {fieldErrors.blockTypeId && (
            <span className={formStyles.fieldError} role="alert">{fieldErrors.blockTypeId}</span>
          )}
        </div>

        <div className={formStyles.fieldHalf}>
          <label className={formStyles.label} htmlFor="blockCourse">
            Golf course <span className={formStyles.required} aria-hidden="true">*</span>
          </label>
          <select
            id="blockCourse"
            className={`${formStyles.select} ${fieldErrors.golfCourseId ? formStyles.inputError : ''}`}
            value={golfCourseId}
            onChange={(e) => {
              setGolfCourseId(e.target.value);
              if (fieldErrors.golfCourseId) setFieldErrors((prev) => ({ ...prev, golfCourseId: '' }));
            }}
            disabled={isBusy}
            required
          >
            <option value="">Select golf course…</option>
            {courses.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
          {fieldErrors.golfCourseId && (
            <span className={formStyles.fieldError} role="alert">{fieldErrors.golfCourseId}</span>
          )}
        </div>
      </div>

      {/* Start Time + End Time — half width each */}
      <div className={formStyles.fieldRow}>
        <div className={formStyles.fieldHalf}>
          <label className={formStyles.label} htmlFor="blockStartTime">
            Start time <span className={formStyles.required} aria-hidden="true">*</span>
          </label>
          <input
            id="blockStartTime"
            type="datetime-local"
            className={`${formStyles.input} ${fieldErrors.startTime ? formStyles.inputError : ''}`}
            value={startTime}
            onChange={(e) => {
              setStartTime(e.target.value);
              if (fieldErrors.startTime) setFieldErrors((prev) => ({ ...prev, startTime: '' }));
              // Clear end-time cross-validation error when start changes
              if (fieldErrors.endTime) setFieldErrors((prev) => ({ ...prev, endTime: '' }));
              // Clear affected bookings whenever user edits
              if (affectedBookings) setAffectedBookings(null);
            }}
            disabled={isBusy}
            required
          />
          {fieldErrors.startTime && (
            <span className={formStyles.fieldError} role="alert">{fieldErrors.startTime}</span>
          )}
        </div>

        <div className={formStyles.fieldHalf}>
          <label className={formStyles.label} htmlFor="blockEndTime">
            End time <span className={formStyles.required} aria-hidden="true">*</span>
          </label>
          <input
            id="blockEndTime"
            type="datetime-local"
            className={`${formStyles.input} ${fieldErrors.endTime ? formStyles.inputError : ''}`}
            value={endTime}
            onChange={(e) => {
              setEndTime(e.target.value);
              if (fieldErrors.endTime) setFieldErrors((prev) => ({ ...prev, endTime: '' }));
              if (affectedBookings) setAffectedBookings(null);
            }}
            disabled={isBusy}
            required
          />
          {fieldErrors.endTime && (
            <span className={formStyles.fieldError} role="alert">{fieldErrors.endTime}</span>
          )}
        </div>
      </div>
    </EntityForm>
  );
}
